import moment from 'moment';

/**
 * Helpers for the "available between X and Y" search filter.
 *
 * Availability lives in the Algolia listing record as a flat `date` array of
 * unix timestamps, two entries per available block:
 *
 *   [block1Start, block1End, block2Start, block2End, ...]
 *
 * Both boundaries are inclusive and are the local midnight of a YYYY-MM-DD
 * day (see EditListingAvailabilityPanel / EditListingPage.duck, which write
 * the array). A listing is available for a requested range only when a single
 * block covers the whole range - i.e. blockStart <= rangeStart and
 * blockEnd >= rangeEnd.
 */

// The indexed timestamps are local midnight in the timezone of whoever saved
// the listing, so a searcher in another timezone can be off by a few hours.
// Half a day of slack absorbs that without ever admitting a different day.
const TZ_SLACK_SECONDS = 12 * 60 * 60;

const DAY_PARAM_FORMAT = 'YYYY-MM-DD';
const DAY_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Serialize a picked day for the URL as YYYY-MM-DD.
 *
 * Deliberately not toISOString(): the picker gives local midnight, and
 * converting that to UTC moves the calendar day for any positive UTC offset.
 */
export const formatDayParam = value => {
  if (!value) {
    return null;
  }
  const day = moment(value instanceof Date ? value : new Date(value));
  return day.isValid() ? day.format(DAY_PARAM_FORMAT) : null;
};

/**
 * Parse a startDate/endDate URL param into a local-midnight moment.
 * Accepts both YYYY-MM-DD and the full ISO strings older links may carry.
 */
export const parseDayParam = value => {
  if (!value) {
    return null;
  }
  const str = String(value);
  const day = DAY_ONLY_PATTERN.test(str)
    ? moment(str, DAY_PARAM_FORMAT)
    : moment(new Date(str));

  return day.isValid() ? day.startOf('day') : null;
};

export const parseDayParamToDate = value => {
  const day = parseDayParam(value);
  return day ? day.toDate() : null;
};

export const parseDayParamToUnix = value => {
  const day = parseDayParam(value);
  return day ? day.unix() : null;
};

/**
 * Coarse Algolia gate for "some availability block covers this range".
 *
 * Deliberately one-sided. Algolia applies several numeric conditions on the
 * same array attribute to the *same element*, so `date <= start AND date >=
 * end` can never match - one timestamp cannot be both before the start and
 * after the end of the range. Verified against the live index: that pair
 * returns 0 hits while each condition on its own returns plenty.
 *
 * A covering block must start at or before the requested start, and its start
 * is an element of `date`, so this single condition is a valid necessary
 * check. isAvailableForRange() then does the exact per-block match on the
 * returned hits.
 */
export const buildAvailabilityFilter = (startUnix, endUnix) => {
  if (!Number.isFinite(startUnix) || !Number.isFinite(endUnix)) {
    return null;
  }
  return `date <= ${startUnix + TZ_SLACK_SECONDS}`;
};

/**
 * Exact check: does a single availability block of this listing cover the
 * whole requested range? `date` pairs up as [start, end] per block.
 */
export const isAvailableForRange = (listing, startUnix, endUnix) => {
  if (!Number.isFinite(startUnix) || !Number.isFinite(endUnix)) {
    return true;
  }

  const dates = Array.isArray(listing?.date) ? listing.date : [];

  for (let i = 0; i + 1 < dates.length; i += 2) {
    const blockStart = Number(dates[i]);
    const blockEnd = Number(dates[i + 1]);

    if (!Number.isFinite(blockStart) || !Number.isFinite(blockEnd)) {
      continue;
    }

    const covers =
      blockStart <= startUnix + TZ_SLACK_SECONDS &&
      blockEnd >= endUnix - TZ_SLACK_SECONDS;

    if (covers) {
      return true;
    }
  }

  return false;
};

/**
 * Reads the availability range out of parsed URL query params.
 * Returns { startUnix, endUnix } with nulls when no range is selected.
 */
export const getAvailabilityRange = ({ startDate, endDate } = {}) => {
  const startUnix = parseDayParamToUnix(startDate);
  const endUnix = parseDayParamToUnix(endDate);

  return startUnix != null && endUnix != null && endUnix >= startUnix
    ? { startUnix, endUnix }
    : { startUnix: null, endUnix: null };
};
